

import React, { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { api } from '../../api/client';
import _ from 'lodash';

import { Alert, Form, Button, App, Checkbox, Grid, Popover, Card, Select, Skeleton, Space } from 'antd';
import { SortableContext, arrayMove, /*horizontalListSortingStrategy,*/ verticalListSortingStrategy } from '@dnd-kit/sortable';

import CollectionFormElement from './CollectionFormElement';
import UserSearchSelect from '../users/UserSearchSelect';
import { InputIconSelect, InputSwitch, InputText, InputTextArea } from '../Input';
import { canManage, getMyAccess, isOwner, roleLabel } from '../../utils/permissions';
import { isArchiveKind, isGlobal, isPersonal } from '../../utils/collectionKinds';
import { HELP_QUESTION_MARK, PLUS } from '../icons';
import './../../css/forms.css';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { API_COLLECTIONS_URL, getUserId, isAdmin } from '../../utils';
import { slugify } from '../../utils/strings';
import { dataTypes } from '../../utils/dictionary';
import CardLibrary from './CardLibrary';
import SharingTab from './SharingTab';
import PublicFormsTab from './PublicFormsTab';
import usePublicForms from '../../hooks/usePublicForms';
import useTabParam from '../../hooks/useTabParam';
import { publicFormsTabShown } from '../../utils/publicForms';
import LinkedCollectionsPanel from './LinkedCollectionsPanel';
import { canSeeLinks, describeCopyFailure } from '../../utils/collectionLinks';
import { getCollectionLinks } from '../../services/collectionLinks';
import { cancelTransferOffer, getCollectionTransferOffer, offerCollection, transferOfferRefusal } from '../../services/transferOffers';
import { formatDisplayDate } from '../../utils/dateDisplay';
import { apiErrorCode, apiErrorMessage } from '../../utils/apiErrors';
import RecordBadge from '../common/RecordBadge';
import { parentOf, subCollectionsOf } from '../../utils/collectionFamily';
import { canDetach, showsSubCollectionsTab, transferBlockedReason } from '../../utils/familyActions';
import DetachSubCollectionModal from './DetachSubCollectionModal';
import SubCollectionsTab from './SubCollectionsTab';
import CollectionTimezoneField from './CollectionTimezoneField';
import CollectionCurrencyField from './CollectionCurrencyField';
import { formatCount, joinMeta } from '../../utils/counts';
import { getCollectionAttachmentsSummary } from '../../services/attachments';
import { getAutomations } from '../../services/automations';
import PublishingTab from './PublishingTab';
import HistoryTab from './HistoryTab';
import CollectionRecentlyDeleted from './CollectionRecentlyDeleted';
import AutomationsTab from './AutomationsTab';
import NotificationsTab from './NotificationsTab';
import ExportsTab from './ExportsTab';
import ExportEverythingTab from './ExportEverythingTab';
import ImportsTab from './ImportsTab';
import AddItemTab from './AddItemTab';
import TagsTab from './TagsTab';
import ExportModal from '../common/ExportModal';
import ContextBar, { ContextBarTabs } from '../common/ContextBar';
import { DataState, Skeleton as LoadingSkeleton, deriveDataState } from '../loading';
import ShellSubNav from '../shell/ShellSubNav';
import { collectionCardsPath, settingsSubNav } from '../../config/collectionShell';
import { ShellActions, ShellSlotContext, useShellMenu, useShellSlot } from '../shell/ShellSlot';
import FormActionsFooter from '../common/FormActionsFooter';
import ConfirmWordModal from '../common/ConfirmWordModal';
import StaleRecordModal from '../common/StaleRecordModal';
import { changedKeys, reapplyOver } from '../../utils/reapply';
import { withStoredItemTagSettings, withoutItemTagKeys } from '../../utils/itemTagSettings';
import { COLLECTIONS_CRUMB, collectionCrumbs } from '../../utils/breadcrumbs';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import {
	getCollectionFacetCardKeys,
	getCollectionHiddenQuickFilters,
	getCollectionMemberCardIds,
	getCollectionQuickFilterPins,
	toCollectionFacets,
	toCollectionQuickFilters,
} from '../../utils/collections';
import { cardRefOf, quickFilterFields, quickFilterKeyNames, quickFilterLabel, quickFilterSettingKey, uniqueCardAliases } from '../../utils/facets';
import { buildFilterFields } from '../../utils/kqlBuilder';
import { normalizeId } from '../../utils/ids';
import { noteCollectionsChanged } from '../../contexts/CollectionsContext';
import { anchorOf, saveFile } from '../../platform';

const normalizeCollectionId = (maybe) => {
	if (!maybe) return null;
	if (typeof maybe === 'string') return maybe;
	if (typeof maybe === 'number') return String(maybe);
	if (typeof maybe === 'object') {
		if (typeof maybe.$oid === 'string') return maybe.$oid;
		if (maybe._id) return normalizeCollectionId(maybe._id);
		if (maybe.id) return normalizeCollectionId(maybe.id);
		try {
			return maybe.toString();
		}
		catch (err) {
			return null;
		}
	}
	return null;
};



// Never part of what the settings PUT sends: ids, lifecycle stamps and the
// response-only annotations the GET adds.
// The family (`parent_id` is an authorization field; `hierarchy` and `parent` are
// read-only summaries) is never the form's to send, nor is `timezoneResolved`
// (the API's computed answer beside the stored `timezone`), nor
// `currencyResolved` (the same, beside `currency`).
const NOT_SENT = [ '_id', 'myAccess', 'updatedAt', 'createdAt', 'lastUpdate', 'createdOn', 'createdBy', 'ownedBy', 'usage', 'parent_id', 'hierarchy', 'parent', 'timezoneResolved', 'currencyResolved' ];

// The words for a top-level setting, for the stale-save dialog.
const SETTING_LABELS = {
	name: 'Name', detail: 'Description', color: 'Color', icon: 'Icon', cards: 'Cards',
	facets: 'Quick filters', quickFilters: 'Quick filters', allowAttachments: 'Attachments',
	definition: 'Elements', tags: 'Tags', timezone: 'Time zone', currency: 'Currency',
};
const settingLabel = key => SETTING_LABELS[ key ] || _.startCase( key );

// The GET's document made whole for the form (a legacy collection may lack
// any of these).
const hydrateCollection = ( doc ) => {
	const out = { ...doc };
	if ( ! out.definition ) out.definition = {};
	if ( ! Array.isArray( out.definition.elements ) ) out.definition = { ...out.definition, elements: [] };
	if ( ! Array.isArray( out.cards ) ) out.cards = [];
	if ( ! Object.prototype.hasOwnProperty.call( out, 'icon' ) ) out.icon = '';
	if ( ! Object.prototype.hasOwnProperty.call( out, 'timezone' ) ) out.timezone = null;
	if ( ! Object.prototype.hasOwnProperty.call( out, 'currency' ) ) out.currency = null;
	return out;
};

/**
 * A saved collection's form, drawn as one of two shell tabs (KPR-1281):
 * Settings (`/form`, the default, its sections in a left sub-nav) or Cards
 * (`section="cards"`, `/cards`), which is the Card Library alone. Both are
 * the same form: the library's add, remove and pin are form changes, so the
 * Cards tab keeps the load, Save changes and stale-save machinery here rather
 * than a second copy of it. Cards is for everyone who reads the collection;
 * the library hides its building actions from a reader itself.
 */
// The settings tabs' address. 'quick-add' was the add-item tab's key before
// multi add joined it. Drill-down state belongs to its tab: leaving it must not
// leave a stale run-history pointer in the URL, and `runsPage` belongs to
// whichever ledger was open, so any tab change drops it.
const SETTINGS_TAB_OPTIONS = {
	aliases: { 'quick-add': 'add-item' },
	clearOnChange: { runsRule: [ 'automations' ], runsExport: [ 'exports' ], runsPage: [] },
};

export default function CollectionForm( { collectionId, section } ) {

	// The id can arrive as a prop or from the route (/collections/:collectionId/form).
	// 'new' (and the legacy '-1' sentinel) mean "create a new collection".
	const routeParams = useParams();
	const [ searchParams ] = useSearchParams();
	const normalizedRouteCollectionId = normalizeCollectionId( collectionId ?? routeParams?.collectionId );
	const hasRouteCollectionId = Boolean( normalizedRouteCollectionId )
		&& normalizedRouteCollectionId !== '-1'
		&& normalizedRouteCollectionId !== 'new';
	const isNewRecord = ! hasRouteCollectionId;
	// The Cards tab: only a saved collection has one.
	const cardsSection = section === 'cards' && hasRouteCollectionId;

	// Ownership is server-set at create and sharing lives on the Sharing
	// endpoints — neither travels with this form's payload any more (the API
	// strips authorization fields from content PUTs regardless).
	const [ collection, setCollection ] = useState( {
		name: '',
		detail: '',
		color: '',
		icon: '',
		allowAttachments: true,
		cards: [],
		definition: {
			elements: [],
			color: '',
		},
		// tags: [],
	} );

	// An existing collection is read before the form opens: 'loading' | 'ready'
	// | 'error' (the read failed: Retry) | 'missing' (the API answered that it
	// is not there, or not this person's). The blank `collection` above is a
	// placeholder, never a draft: nothing but 'ready' draws the form, offers a
	// save or an export, so it cannot be saved over the real collection
	// (docs/platform-standards.md §14). A new collection has nothing to read.
	const [ loadState, setLoadState ] = useState( hasRouteCollectionId ? 'loading' : 'ready' );
	const [ loadAttempt, setLoadAttempt ] = useState( 0 );
	const awaiting = hasRouteCollectionId && loadState !== 'ready';

	const [ saveStatus, setSaveStatus ] = useState( 'idle' );
	const [ exportOpen, setExportOpen ] = useState( false );
	// "Save this export…" hands a stored export's starting point to the
	// Exports tab — from the list's Export view… it arrives in router state
	// (a payload, not an address: a URL carrying export options would mean
	// something else when pasted), from this page's own Export… directly.
	// Read once and cleared, so a refresh does not reopen the editor.
	const location = useLocation();
	const [ exportPrefill, setExportPrefill ] = useState( null );
	const { isDirty, resetDirty, allowNavigation } = useDirtyForm( collection );
	const resetDirtyRef = useRef( resetDirty );
	// The collection AS THIS FORM LOADED IT (a deep copy: the legacy element
	// editor below mutates in place). A save sends only the top-level
	// settings that differ from it, plus `ifUpdatedAt` from it, so a form
	// opened before a colleague added a card never PUTs back the old
	// `cards[]` (delete audit F6). It moves on with every load and save.
	const baseRef = useRef( null );
	// The open "Someone else changed this collection" dialog.
	const [ stale, setStale ] = useState( null );

	useEffect( () => {
		resetDirtyRef.current = resetDirty;
	}, [ resetDirty ] );

	const markPristine = useCallback( ( nextSnapshot ) => {
		resetDirtyRef.current( nextSnapshot );
	}, [] );

	const navigate = useNavigate();

	const { message, modal } = App.useApp();
	// Read through a ref so a changed `message` identity can never re-run the
	// initial read (and with it drop what the person has typed).
	const messageRef = useRef( message );
	messageRef.current = message;

	useEffect( () => {
		const incoming = location.state?.exportPrefill;
		if ( ! incoming ) return;
		setExportPrefill( incoming );
		navigate( `${ location.pathname }${ location.search }`, { replace: true, state: null } );
	}, [ location.state ] ); // eslint-disable-line react-hooks/exhaustive-deps




	const cleanPrevKeys = ( obj, forceDelete ) => {
		// TODO - temporary to fix issue with adding new choices to element

		const elements = obj.definition.elements;

		for ( var eIdx = 0; eIdx < elements.length; ++eIdx ) {
			if ( elements[ eIdx ].hasOwnProperty( 'choices' ) ) {
				for ( var cIdx = 0; cIdx < elements[ eIdx ].choices.length; ++cIdx ) {
					if ( forceDelete || 
						elements[ eIdx ].choices[ cIdx ].prevValue === '' ||
						elements[ eIdx ].choices[ cIdx ].prevValue === elements[ eIdx ].choices[ cIdx ].value ) {
						delete elements[ eIdx ].choices[ cIdx ].prevValue;
					}
				}
			}
		}

		obj.definition.elements = elements;

		return obj;
	};

	const resolvedCollectionId = hasRouteCollectionId ? normalizedRouteCollectionId : normalizeCollectionId( collection?._id );

	const exportTarget = useMemo(
		() => ( resolvedCollectionId ? { kind: 'collection', collectionId: resolvedCollectionId } : null ),
		[ resolvedCollectionId ]
	);



	// GET COLLECTION (also re-run after archive/unarchive so the fresh status
	// and myAccess come straight from the server, not a local guess)
	const loadCollection = useCallback( ( id, { initial = false, isCurrent = () => true } = {} ) => {
		return api.get( API_COLLECTIONS_URL + '/' + id ).then( data => {
			if ( ! isCurrent() ) return;
			if ( data.status !== 200 ) {
				if ( initial ) setLoadState( 'error' );
				return;
			}

			// validate 'elements'
			if ( ! data.data.hasOwnProperty( 'definition' ) ) data.data.definition = {};
			if ( ! data.data.definition.hasOwnProperty( 'elements' ) ) data.data.definition.elements = [];
			if ( ! data.data.hasOwnProperty( 'cards' ) ) data.data.cards = [];
			if ( ! data.data.hasOwnProperty( 'icon' ) ) data.data.icon = '';
			// Inheriting is spelled null in the form, never absent: the reset
			// must send `timezone: null` (a dropped key changes nothing on the
			// API), and a zone picked and reset again must read as unchanged.
			if ( ! data.data.hasOwnProperty( 'timezone' ) ) data.data.timezone = null;
			if ( ! data.data.hasOwnProperty( 'currency' ) ) data.data.currency = null;

			const hydrated = cleanPrevKeys( data.data, true );
			baseRef.current = _.cloneDeep( hydrated );
			setCollection( hydrated );
			markPristine( hydrated );
			if ( initial ) setLoadState( 'ready' );

		} ).catch( error => {
			if ( ! isCurrent() ) return;
			if ( initial ) {
				// Only an answer is a verdict: a 404/403 is a collection that is not
				// there (or not theirs); no response, or a 5xx, is worth another try.
				setLoadState( [ 403, 404 ].includes( error?.response?.status ) ? 'missing' : 'error' );
				return;
			}
			// A re-read after archive, restore or detach: the form keeps what it holds.
			messageRef.current.error( 'Could not refresh this collection. Reload the page to see its latest state.' );
		} );
		// eslint-disable-next-line react-hooks/exhaustive-deps -- cleanPrevKeys is stable
	}, [ markPristine ] );

	useEffect( () => {

		if ( hasRouteCollectionId && normalizedRouteCollectionId ) {
			// A stale answer for an earlier collection (or attempt) must not win.
			let current = true;
			setLoadState( 'loading' );
			loadCollection( normalizedRouteCollectionId, { initial: true, isCurrent: () => current } );
			return () => { current = false; };
		}
		return undefined;

	}, [ normalizedRouteCollectionId, hasRouteCollectionId, loadCollection, loadAttempt ] );

	// How many rules an assistant made wait for this person (KPR-196): said on
	// the Automations tab's own label, before the tab is opened. Manager-only,
	// like the tab; the tab itself reports a fresh count after every change.
	const [ waitingRules, setWaitingRules ] = useState( 0 );
	const managesHere = Boolean( collection?._id ) && canManage( collection );
	// Permanent public forms (KPR-513): asked once, by a manager of a saved
	// collection, because the answer decides whether the Public forms tab
	// exists at all — an API without them (404) or with its switch off shows
	// nothing new (hooks/usePublicForms).
	// Managed HERE: the loaded record is the one the route names (a stale
	// record from the collection navigated away from would send a reader of
	// this one to a manage route).
	const managesRouteCollection = managesHere && normalizeId( collection?._id ) === normalizedRouteCollectionId;
	const publicForms = usePublicForms( hasRouteCollectionId ? normalizedRouteCollectionId : null, { enabled: managesRouteCollection && ! cardsSection } );
	useEffect( () => {
		// Another collection's count never stands on this one's tab (F13).
		setWaitingRules( 0 );
		// The count is for Settings' Automations section; the Cards tab has none.
		if ( ! hasRouteCollectionId || ! normalizedRouteCollectionId || ! managesHere || cardsSection ) return undefined;
		let active = true;
		// Through a promise of its own: a missing answer is no count, never a throw.
		Promise.resolve().then( () => getAutomations( normalizedRouteCollectionId ) ).then(
			rules => { if ( active ) setWaitingRules( ( Array.isArray( rules ) ? rules : [] ).filter( rule => rule?.awaitingPerson && rule?.enabled === false && ! rule?.inherited ).length ); },
			() => {}
		);
		return () => { active = false; };
	}, [ hasRouteCollectionId, normalizedRouteCollectionId, managesHere, cardsSection ] );



	const handleSubmit = ( e ) => {

		if ( awaiting ) return;
		if ( ! isDirty ) return;
		setSaveStatus( 'saving' );

		const targetCollectionId = resolvedCollectionId;

		if ( targetCollectionId ) {
			// Update: ONLY the top-level settings this form changed (the PUT is
			// a `$set` of its body, so a partial body is the contract), plus
			// `ifUpdatedAt` — the collection's `updatedAt` as loaded. `cards`
			// rides along only when the member list itself was edited, and
			// then whole. A colleague's save in between is a 409
			// `stale_collection` (below), never an overwrite. myAccess and
			// the other response-only fields are never sent.
			const base = baseRef.current;
			const keys = base ? changedKeys( base, collection, NOT_SENT ) : Object.keys( collection ).filter( key => ! NOT_SENT.includes( key ) );
			if ( ! keys.length ) {
				markPristine( collection );
				setSaveStatus( 'idle' );
				return;
			}
			const payload = _.pick( collection, keys );
			// A card's Use items as tags is the item-tags endpoint's alone
			// (T4d): a PUT that leaves those keys out carries the stored
			// values through, and one that sends a value the endpoint has
			// changed since is refused — so they are never sent.
			if ( Array.isArray( payload.cards ) ) payload.cards = withoutItemTagKeys( payload.cards );
			if ( base?.updatedAt ) payload.ifUpdatedAt = base.updatedAt;
			const sent = _.cloneDeep( collection );
			api.put( API_COLLECTIONS_URL + '/' + targetCollectionId,
				payload ).then( data => {
					
					if ( data.status !== 200 ) {
						return;
					}

					noteCollectionsChanged();
					message.success( 'Collection saved' );
					markPristine( collection );
					setSaveStatus( 'idle' );

					// The base moves to what was just saved. The PUT answers
					// with the write's status, not the document, so the new
					// `updatedAt` is read back when the answer lacks it.
					baseRef.current = { ...sent, updatedAt: data.data?.updatedAt || base?.updatedAt || null };
					if ( ! data.data?.updatedAt ) {
						api.get( API_COLLECTIONS_URL + '/' + targetCollectionId ).then( fresh => {
							if ( fresh?.status === 200 && fresh.data?.updatedAt && baseRef.current ) {
								baseRef.current = { ...baseRef.current, updatedAt: fresh.data.updatedAt };
							}
						} ).catch( () => {} );
					}

				} ).catch( error => {
					setSaveStatus( 'failed' );
					const data = error?.response?.data;
					if ( error?.response?.status === 409 && data?.code === 'stale_collection' ) {
						if ( ! data.collection ) {
							message.error( 'This collection was deleted, or you can no longer manage it, so your changes were not saved.' );
							return;
						}
						const theirs = cleanPrevKeys( hydrateCollection( data.collection ), true );
						const { merged, overlap } = reapplyOver( {
							base: base || {},
							mine: collection,
							theirs,
							ignore: NOT_SENT,
							lists: { cards: entry => normalizeId( entry?.cardDefinitionId ) },
						} );
						setStale( {
							overlap: [ ...new Set( overlap.map( settingLabel ) ) ],
							theirs,
							merged,
						} );
						return;
					}
					// A card whose items are used as tags cannot leave the
					// collection until that is turned off (409 item_tags_on).
					if ( data?.code === 'item_tags_on' ) {
						message.error( 'The collection was not saved: a card you removed has its items used as tags here. Turn Use items as tags off for it first, on the Cards tab.' );
						return;
					}
					message.error( data?.code === 'invalid_if_updated_at' || ( error?.response?.status === 400 && data?.message )
						? `The collection was not saved: ${ data.message }`
						: 'Something went wrong. The collection may not have saved' );
				} );
			return;
		}

		// insert new. ownedBy rides along ONLY as pre-grants-API compatibility:
		// the grants-era API ignores it and sets ownership server-side.
		api.post( API_COLLECTIONS_URL,
			{ ...collection, ownedBy: getUserId() } ).then( data => {
				if ( data.status !== 201 ) {
					return;
				}

				noteCollectionsChanged();
				const insertedId = normalizeCollectionId( data.data?.insertedId || data.data?._id || data.data?.id );

				setCollection( prevState => {
					const nextState = insertedId ? { ...prevState, _id: insertedId } : prevState;
					markPristine( nextState );
					return nextState;
				} );
				setSaveStatus( 'idle' );
				message.success( 'Collection saved' );

				if ( insertedId ) {
					// Saved: the move to the new collection's own address is not
					// leaving unsaved work (the pristine mark above runs inside a
					// state updater, which React may not have run yet).
					allowNavigation();
					navigate( '/collections/' + insertedId + '/form', { replace: true } );
				}
			} ).catch( error => {
				setSaveStatus( 'failed' );
				message.error( 'Something went wrong. The collection may not have saved.' );
			} );

	};



	// The stale-save dialog's two ways on. Reapply: the collection as it
	// stands becomes the base and this person's edits go back on top of it
	// (utils/reapply.js — `cards` merged by member, so a colleague's new
	// member survives), the form left dirty to check and save again.
	// Discard: the collection as it stands.
	const reapplyStale = () => {
		if ( ! stale ) return;
		baseRef.current = _.cloneDeep( stale.theirs );
		markPristine( stale.theirs );
		setCollection( stale.merged );
		setSaveStatus( 'idle' );
		setStale( null );
	};
	// After a restore from Settings › Recently deleted, or a sub-collection
	// attached or detached on the Sub-collections tab: the collection moved
	// under this form (a restored card is back in `cards[]`, `updatedAt` moved,
	// `hierarchy` lists a different family), so its base is re-read — otherwise
	// the next save carries the old `ifUpdatedAt` and is refused as stale by a
	// change this person made themselves. A pristine form simply reloads; a
	// dirty one gets the stale-save reapply without the dialog: the fresh
	// collection as the base, this person's edits back on top (`cards` merged
	// by member).
	//
	// The same after a card's Use items as tags changed (Settings › Cards,
	// T4d): its endpoint moved `cards[]` and `updatedAt`. The item-tag keys
	// always take the collection's — an entry this person edited meanwhile
	// keeps their edit but shows what the endpoint wrote.
	//
	// Also after the card library deleted or promoted a card (it writes at
	// once): the library has already mirrored the change into `cards[]`, and
	// this re-baselines on the server's copy so that mirror is not an unsaved
	// edit. `mine` and the dirty check are read when the answer arrives, not
	// from the render that created the callback: the library's own edit has
	// landed by then, and a callback it holds is a render behind.
	const latestFormRef = useRef( {} );
	latestFormRef.current = { collection, isDirty };
	const refreshKeepingEdits = () => {
		if ( ! resolvedCollectionId ) return;
		api.get( API_COLLECTIONS_URL + '/' + resolvedCollectionId ).then( fresh => {
			if ( fresh?.status !== 200 || ! fresh.data ) return;
			const { collection: mine, isDirty: dirtyNow } = latestFormRef.current;
			if ( ! dirtyNow ) {
				loadCollection( resolvedCollectionId );
				return;
			}
			const theirs = cleanPrevKeys( hydrateCollection( fresh.data ), true );
			const { merged } = reapplyOver( {
				base: baseRef.current || {},
				mine,
				theirs,
				ignore: NOT_SENT,
				lists: { cards: entry => normalizeId( entry?.cardDefinitionId ) },
			} );
			if ( Array.isArray( merged.cards ) ) merged.cards = withStoredItemTagSettings( merged.cards, theirs.cards );
			baseRef.current = _.cloneDeep( theirs );
			markPristine( theirs );
			setCollection( merged );
		} ).catch( () => {} );
	};

	const discardStale = () => {
		if ( ! stale ) return;
		baseRef.current = _.cloneDeep( stale.theirs );
		setCollection( stale.theirs );
		markPristine( stale.theirs );
		setSaveStatus( 'idle' );
		setStale( null );
	};

	// `copyToLinks: false` — the dialog's box unticked — ends every outgoing
	// link without the keep-copy its "Recipients may keep copies" promised
	// (keepr-api § When a link ends). Absent, the copies are made first.
	const handleDelete = ( { copyToLinks = true } = {} ) => {
		if ( ! resolvedCollectionId ) return;

		const url = API_COLLECTIONS_URL + '/' + resolvedCollectionId;
		( copyToLinks ? api.delete( url ) : api.delete( url, { params: { copyToLinks: false } } ) )
			.then( data => {
				console.log( JSON.stringify( data, null, '\t' ) );
				if ( data.status !== 200 ) {
					return;
				}

				noteCollectionsChanged();
				// A parent takes its sub-collections with it (the API lists them).
				const gone = Array.isArray( data?.data?.deleted ) ? data.data.deleted.length : 0;
				message.success( gone ? `Collection and ${ formatCount( gone, 'sub-collection' ) } deleted` : 'Collection deleted' );
				markPristine();

				navigate( '/collections' );
			} )
			.catch( error => {
				if ( apiErrorCode( error ) === 'link_copy_failed' ) {
					const words = describeCopyFailure( null, 'delete' );
					modal.error( { title: words.title, content: words.body, okText: 'OK' } );
					return;
				}
				// The API's own words when it says why (an offer waiting on it, an
				// unconfirmed address — KPR-1267), else the generic line.
				message.error( apiErrorMessage( error, 'Could not delete this collection.' ) );
			} );

	};

	const handleArchive = () => {
		if ( ! resolvedCollectionId ) return;

		api.post( API_COLLECTIONS_URL + '/' + resolvedCollectionId + '/archive' )
			.then( data => {
				if ( data.status !== 200 ) return;
				noteCollectionsChanged();
				message.success( 'Collection archived — items are now read-only' );
				loadCollection( resolvedCollectionId );
			} )
			.catch( error => {
				message.error( apiErrorMessage( error, 'Could not archive this collection.' ) );
			} );
	};

	const handleUnarchive = () => {
		if ( ! resolvedCollectionId ) return;

		api.post( API_COLLECTIONS_URL + '/' + resolvedCollectionId + '/unarchive' )
			.then( data => {
				if ( data.status !== 200 ) return;
				noteCollectionsChanged();
				message.success( 'Collection unarchived' );
				loadCollection( resolvedCollectionId );
			} )
			.catch( error => {
				// A sub-collection under an archived parent comes back only
				// with it (409 parent_archived): the API says so.
				message.error( apiErrorMessage( error, 'Could not unarchive this collection.' ) );
			} );
	};

	// A transfer is an OFFER (KPR-743): nothing changes until the person
	// accepts, so the owner stays on this page and the Danger zone shows the
	// offer waiting, with Cancel. Resolves to the offer, or null when refused.
	const handleTransfer = ( newOwnerId ) => {
		if ( ! resolvedCollectionId || ! newOwnerId ) return Promise.resolve( null );

		return offerCollection( resolvedCollectionId, newOwnerId )
			.then( offer => {
				const who = offer?.recipient?.name || offer?.recipient?.email || 'them';
				message.success( `Offered to ${ who }. Nothing changes until they accept.` );
				return offer;
			} )
			.catch( error => {
				message.error( transferOfferRefusal( error, 'Could not offer this collection. Please try again.' ) );
				return null;
			} );
	};



	const handleChange = ( name, value ) => setCollection(prevState => ( { ...prevState, [name]: value } ) );
	


	// Admin-only raw export, as an entry in the ⋯ menu (the header keeps
	// only the record's own actions).
	const handleExportJson = async () => {
		try {
			const blob = new Blob( [ JSON.stringify( collection, null, '\t' ) ], { type: 'application/json' } );
			await saveFile( blob, `keepr-collection-${ slugify( collection?.name || '' ) || resolvedCollectionId || 'export' }.json`, { anchor: anchorOf() } );
		}
		catch ( err ) {
			message.error( 'Could not export this collection.' );
		}
	};

	// Ownership/access context moved out of the General form into the ⋯
	// menu — it is information, not a setting.
	const ownershipLine = isOwner( collection )
		? ( isPersonal( collection ) ? 'You own this personal collection.' : 'You own this collection.' )
		: ( roleLabel( collection ) ? `Your access: ${ roleLabel( collection ) }` : null );

	// The bar owns the ⋯ (and adds Help to it); these are this page's own
	// entries, in the platform taxonomy.
	const overflowActions = awaiting ? [] : [
		ownershipLine
			? { key: 'access', group: 'record', label: ownershipLine, disabled: true }
			: null,
		// The server-rendered export (PDF/JSON preview + download); the
		// admin-only "Export JSON" below it is the raw client dump, and it
		// lives in this menu too rather than as its own header button.
		! isNewRecord && resolvedCollectionId
			? { key: 'export', group: 'share', glyph: 'export', label: 'Export…', onClick: () => setExportOpen( true ) }
			: null,
		isAdmin() && ! isNewRecord
			? { key: 'export-json', group: 'share', glyph: 'export', label: 'Export JSON', onClick: handleExportJson }
			: null,
	].filter( Boolean );

	// Inside the collection shell the page draws no header: the shell's tabs
	// carry Settings, the left sub-nav carries the sections, and the page gives
	// the shell its title, its Save and its menu entries. Rendered bare (the
	// create route, a test) it keeps its own context bar.
	const inShell = Boolean( useContext( ShellSlotContext ) ) && ! isNewRecord;
	const phoneShell = Grid.useBreakpoint().md === false;
	// The ⋯ entries (access, Export…) are Settings'; the Cards tab adds none.
	useShellMenu( inShell ? ( cardsSection ? [] : overflowActions ) : null );
	useShellSlot( inShell ? { title: ( collection?.name || '' ).trim() || null } : {} );

	// Below manage the page is reachable but read-only: inputs disabled, no
	// Save/delete, and the manager-only tabs (Card Library, Tags …) hidden. The
	// server enforces the same rule (collection PUT requires manage). Gated on
	// the LOADED document (which carries myAccess) so owners don't see a
	// read-only flash while the fetch is in flight.
	const collectionLoaded = Boolean( collection?._id );
	const readOnly = ! isNewRecord && collectionLoaded && ! canManage( collection );
	// The lifecycle operations (delete, archive) are a manager's on the server
	// (KPR-1267: a collection's managers may do everything its owner may,
	// except transfer it); transfer alone stays the owner's, so the tab shows
	// its row to the owner (and admins) only. A personal collection has no
	// Danger zone at all: the API refuses all three for everyone (409
	// personal_collection_locked) — it leaves only with its account.
	const canDestroy = ( canManage( collection ) || getMyAccess( collection ).isAdmin ) && ! isPersonal( collection );
	const canTransfer = isOwner( collection ) || getMyAccess( collection ).isAdmin === true;
	const isArchived = collection?.status === 'archived';

	// The primary, defined once: the context bar shows it at the top, the
	// Settings tab (the one long form body — the other tabs apply their
	// changes through their own endpoints) repeats it at the end of its
	// column through FormActionsFooter. Read-only viewers get neither.
	const formActions = ( readOnly || awaiting ) ? null : (
		<Button
			type="primary"
			onClick={handleSubmit}
			loading={saveStatus === 'saving'}
			disabled={!isDirty || saveStatus === 'saving'}
		>
			{saveStatus === 'failed' ? 'Retry save' : (isNewRecord ? 'Create collection' : 'Save changes')}
		</Button>
	);

	// The active tab lives in the URL (?tab=automations) so a refresh — or a
	// link pasted to another manager, e.g. straight into a rule's run history —
	// lands on the same tab instead of Settings. A param naming a tab the
	// caller can't see (read-only user given a manager's link) falls back to
	// Settings rather than rendering nothing.
	// Export everything (KPR-742): every item and file the person can read, as
	// one zip — a reader's right as much as a manager's, so it is a section for
	// everyone who can open a saved collection, beside Exports for a manager.
	const exportEverythingTab = isNewRecord ? [] : [ {
		key: 'export-everything',
		label: 'Export everything',
		children: <ExportEverythingTab collectionId={resolvedCollectionId} collection={collection} />,
	} ];
	const cardLibraryTab = {
		key: 'card-library',
		label: 'Card Library',
		children: <CardLibrary collection={collection} setCollection={setCollection} onReload={() => loadCollection( resolvedCollectionId )} onItemTagsChanged={refreshKeepingEdits} onCardsChangedOnServer={refreshKeepingEdits} />
	};
	const allTabsItems = cardsSection ? [ cardLibraryTab ] : [
		{
			key: 'settings',
			label: 'Settings',
			children: <FormSettings collection={collection} handleChange={handleChange} readOnly={readOnly} footer={<FormActionsFooter>{formActions}</FormActionsFooter>} />,
		},
		// {
		// 	key: 'card-builder',
		// 	label: 'Card Builder',
		// 	children: <FormCards collection={collection} setCollection={setCollection} />,
		// },
		// Below manage, the Sharing tab holds one thing: what this collection
		// reads from other collections by link — read-only, for anyone with
		// collection-level access (a card or filtered grantee cannot list
		// links; the API answers them 403).
		...( ( readOnly && ! isNewRecord && canSeeLinks( collection ) ) ? [ {
			key: 'sharing',
			label: 'Sharing',
			children: <LinkedCollectionsPanel collectionId={resolvedCollectionId} collection={collection} readOnly />,
		} ] : [] ),
		...( readOnly ? exportEverythingTab : [] ),
		...( readOnly ? [] : [
			// A saved collection's cards are its Cards tab (KPR-1281), not a
			// Settings section. A collection being created has no tabs yet, so
			// it chooses its first cards here, beside its settings.
			...( isNewRecord ? [ cardLibraryTab ] : [] ),
			// A parent's family in one place: its sub-collections, each with
			// doors and Detach…, and New sub-collection… · Attach an existing
			// collection…. Everything on it applies at once, through its own
			// modals. Never on a sub-collection (it cannot have any), nor on a
			// collection that has none and cannot take one — a personal,
			// archived or global collection — where the tab would be a dead end.
			...( ( ! isNewRecord && showsSubCollectionsTab( collection ) ) ? [ {
				key: 'sub-collections',
				label: 'Sub-collections',
				children: <SubCollectionsTab collection={collection} onChanged={refreshKeepingEdits} />,
			} ] : [] ),
			// Sharing applies immediately through its own endpoints — it lives
			// inside the settings page but outside the Save-button lifecycle,
			// and only exists for saved collections.
			...( isNewRecord ? [] : [ {
				key: 'sharing',
				label: 'Sharing',
				children: <SharingTab collectionId={resolvedCollectionId} collection={collection} formVolume={[ 'on', 'off' ].includes( publicForms.status )} reviewRules={publicForms.status === 'on'} publicForms={publicForms} />,
			} ] ),
			// Its permanent public forms (KPR-513; ruling f01): their own
			// section, apart from the invite links that stay under Sharing.
			// Where the API has them and its switch is on — and, while the
			// server's switch is off, where a package prepared one here
			// (KPR-360): its draft is shown NOT LIVE, with why nothing can
			// make it live yet, rather than hidden.
			...( ( isNewRecord || ! publicFormsTabShown( publicForms.status, publicForms.data ) ) ? [] : [ {
				key: 'public-forms',
				label: 'Public forms',
				children: <PublicFormsTab collectionId={resolvedCollectionId} collection={collection} publicForms={publicForms} />,
			} ] ),
			// Publishing — making it a global collection — is its managers'
			// (KPR-1267; the owner is told when someone else submits it), for a
			// saved collection that is neither personal nor a keepr archive;
			// it applies immediately, like Sharing.
			// Never on a sub-collection: the API refuses to publish one
			// (409 kind_cannot_nest), so the tab would be a dead end.
			...( ( isNewRecord || ! canManage( collection ) || isPersonal( collection ) || isArchiveKind( collection ) || parentOf( collection ) ) ? [] : [ {
				key: 'publishing',
				label: 'Publishing',
				children: <PublishingTab collectionId={resolvedCollectionId} collection={collection} />,
			} ] ),
			// Automations sits with Sharing rather than with the card tabs for
			// the same two reasons: its endpoints are manage-gated and apply
			// immediately (outside the Save button), and a rule needs a saved
			// collection to be scoped to.
			...( isNewRecord ? [] : [ {
				key: 'automations',
				label: waitingRules ? `Automations · ${ waitingRules } waiting` : 'Automations',
				children: <AutomationsTab collectionId={resolvedCollectionId} collection={collection} onWaitingCount={setWaitingRules} />,
			} ] ),
			// Notifications sit next to Automations because a notification IS an
			// automation of its own kind — same engine, same manage gate, same
			// apply-immediately endpoints — and a manager who has just written a
			// rule is one tab away from deciding how it reaches people.
			...( isNewRecord ? [] : [ {
				key: 'notifications',
				label: 'Notifications',
				children: <NotificationsTab collectionId={resolvedCollectionId} collection={collection} />,
			} ] ),
			// Stored exports sit with Automations and Notifications: the same
			// manage gate, the same apply-immediately endpoints, the same
			// cadence — an export IS a scheduled thing, not a download button.
			...( isNewRecord ? [] : [ {
				key: 'exports',
				label: 'Exports',
				children: <ExportsTab
					collectionId={resolvedCollectionId}
					collection={collection}
					prefill={exportPrefill}
					onPrefillUsed={() => setExportPrefill( null )}
				/>,
			} ] ),
			...exportEverythingTab,
			// Imports sit beside Exports: batch writes by an assistant or a
			// script, grouped by the id their client sent, each with Undo
			// import behind its ⋯. Session-only and manage on the API.
			...( isNewRecord ? [] : [ {
				key: 'imports',
				label: 'Imports',
				children: <ImportsTab collectionId={resolvedCollectionId} collection={collection} />,
			} ] ),
			// The Add item launchers (quick add templates, multi add forms)
			// apply immediately too, and are bound to a saved collection's cards.
			...( isNewRecord ? [] : [ {
				key: 'add-item',
				label: 'Add item',
				children: <AddItemTab collectionId={resolvedCollectionId} collection={collection} />,
			} ] ),
			// The collection's tags (docs/plans/tags.md §5.2): manage-gated on
			// the API, applied immediately, and a sub-collection's tab lists
			// its parent's tags read-only beneath its own.
			...( isNewRecord ? [] : [ {
				key: 'tags',
				label: 'Tags',
				children: <TagsTab collectionId={resolvedCollectionId} collection={collection} />,
			} ] ),
			// The change feed: manage-gated on the API like Sharing and
			// Automations, and only a saved collection has one.
			...( isNewRecord ? [] : [ {
				key: 'history',
				label: 'History',
				children: <HistoryTab collectionId={resolvedCollectionId} />,
			} ] ),
			// Its deleted cards, element sets and items, each with Restore
			// (delete audit F5) — the same manage gate as History.
			...( isNewRecord ? [] : [ {
				key: 'deleted',
				label: 'Recently deleted',
				children: <CollectionRecentlyDeleted collectionId={resolvedCollectionId} onRestored={refreshKeepingEdits} />,
			} ] ),
			// Destructive actions get their own explicit tab — nothing
			// destructive hides behind a ⋯ alone. Its managers', like the API;
			// Transfer only for the owner.
			...( ( isNewRecord || ! canDestroy ) ? [] : [ {
				key: 'danger',
				label: 'Danger zone',
				children: <DangerZone
					collection={collection}
					canTransfer={canTransfer}
					ownsIt={isOwner( collection )}
					globalLocked={isGlobal( collection )}
					onDelete={handleDelete}
					onArchive={handleArchive}
					onUnarchive={handleUnarchive}
					onTransfer={handleTransfer}
					onDetached={() => loadCollection( resolvedCollectionId )}
				/>,
			} ] ),
		] ),
	];

	// Until the collection has answered nobody knows which sections this person
	// may see (a reader gets fewer), so only Settings is named, and what it holds
	// is the skeleton or the failure below, never a form.
	const tabsItems = awaiting ? allTabsItems.slice( 0, 1 ) : allTabsItems;

	// The tab is in the address (hooks/useTabParam): `?tab=` naming a section this
	// person lacks reads as Settings, and Settings is the bare route.
	const [ requestedTab, handleTabChange ] = useTabParam( tabsItems.map( tab => tab.key ), 'settings', SETTINGS_TAB_OPTIONS );
	const activeTabKey = cardsSection ? 'card-library' : requestedTab;

	// The Card Library was a Settings section until KPR-1281: an old link (a
	// bookmark, a pasted ?tab=card-library) lands on the Cards tab, its search kept.
	if ( hasRouteCollectionId && ! cardsSection && searchParams.get( 'tab' ) === 'card-library' ) {
		const q = searchParams.get( 'q' );
		return <Navigate replace to={`${ collectionCardsPath( normalizedRouteCollectionId ) }${ q ? `?q=${ encodeURIComponent( q ) }` : '' }`} />;
	}

	return (<>

		{inShell ? (
			<ShellActions>{formActions}</ShellActions>
		) : (
		/* ONE context bar: breadcrumb Collections / {name} / Settings over the
		    collection's own chip + title (the same shape as the items list —
		    the chip previews the identity live as it is edited), the section
		    tabs as the bar's second row, and the action grammar: ⋯ (which
		    carries the admin-only raw "Export JSON" dump) · Save changes.
		    The create route renders outside CollectionsLayout, so it draws the same bar itself. */
		<ContextBar
			breadcrumb={isNewRecord
				? [COLLECTIONS_CRUMB, { label: 'New' }]
				: collectionCrumbs(collection, resolvedCollectionId, 'Settings')}
			record={{ icon: collection?.icon, color: collection?.color, name: collection?.name }}
			kind="collection"
			title={( collection?.name || '' ).trim() || (isNewRecord ? 'New collection' : 'Collection')}
			actions={readOnly ? null : formActions}
			menuActions={readOnly ? null : overflowActions}
		>
			{tabsItems.length > 1 ? (
				<ContextBarTabs
					aria-label="Settings sections"
					items={tabsItems.map( ( { key, label } ) => ( { key, label } ) )}
					activeKey={activeTabKey}
					onChange={handleTabChange}
				/>
			) : null}
		</ContextBar>
		)}

		{readOnly && ! cardsSection ? (
			<Alert
				type="info"
				showIcon
				className="m-1"
				message="View only"
				description="You don't manage this collection, so its settings are read-only here."
			/>
		) : null}

		{isArchived ? (
			<Alert
				type="warning"
				showIcon
				className="m-1"
				message="Archived"
				description={canDestroy
					? 'This collection is archived: its items are read-only for everyone. Unarchive it from the Danger zone tab to allow changes again.'
					: 'This collection is archived: its items are read-only for everyone.'}
			/>
		) : null}

		<div className={inShell ? `shell-subpage${ phoneShell ? ' shell-subpage--phone' : '' }` : undefined}>
			{inShell && ! cardsSection && tabsItems.length > 1 ? (
				<ShellSubNav
					label="Settings sections"
					activeKey={activeTabKey}
					onSelect={handleTabChange}
					items={settingsSubNav( { collectionId: resolvedCollectionId, sections: tabsItems } )}
				/>
			) : null}
			{/* A reader on the Cards tab still opens and shares cards: the library
			    leaves out what they cannot do, so its form is not disabled. */}
			<Form autoComplete='off' disabled={readOnly && ! cardsSection} className={inShell ? 'shell-subpage__content' : undefined}>
				{/* Only the active section renders: the tabs live in the bar, or in the shell's sub-nav. */}
				{awaiting ? (
					loadState === 'missing' ? (
						<Alert
							type="error"
							showIcon
							className="m-1"
							message="That collection is not available."
							description="It may have been deleted, or it is not yours to open."
							action={<Button onClick={() => navigate( '/collections' )}>Go to collections</Button>}
						/>
					) : (
						<DataState
							state={deriveDataState( {
								loading: loadState === 'loading',
								error: loadState === 'error' ? 'Check your connection and try again.' : null,
							} )}
							skeleton={<LoadingSkeleton variant="form-field" count={6} />}
							error={loadState === 'error' ? 'Check your connection and try again.' : null}
							onRetry={() => setLoadAttempt( n => n + 1 )}
							label="this collection"
						/>
					)
				) : (
					<div role="tabpanel" className="settings-tabpanel">
						{tabsItems.find( tab => tab.key === activeTabKey )?.children}
					</div>
				)}
			</Form>
		</div>

		<StaleRecordModal
			open={Boolean( stale )}
			noun='collection'
			overlap={stale?.overlap || []}
			onReapply={reapplyStale}
			onDiscard={discardStale}
			onClose={() => setStale( null )}
		/>

		<ExportModal
			open={exportOpen}
			onClose={() => setExportOpen( false )}
			target={exportTarget}
			title='Export collection'
			baseName={collection?.name || 'collection'}
			onSaveAsScheduled={! readOnly && ! isNewRecord && resolvedCollectionId ? ( ( { format, options } ) => {
				setExportOpen( false );
				setExportPrefill( {
					name: `${ collection?.name || 'Collection' } outline`.slice( 0, 100 ),
					subject: { kind: 'collection' },
					format,
					options,
				} );
				handleTabChange( 'exports' );
			} ) : null}
		/>

	</>);

};

/**
 * The Danger zone tab: destructive actions as explicit rows, each behind a
 * typed confirmation (archive → 'archive', transfer → 'transfer', delete →
 * 'delete' for the owner and the collection's NAME for a manager who is not
 * its owner — KPR-1267, owner ruling m01). Its managers' (a collection's
 * managers may do what its owner may), with the Transfer row only when
 * `canTransfer`: the one act that stays the owner's.
 */
const DangerZone = ( { collection, canTransfer = false, ownsIt = false, onDelete, onArchive, onUnarchive, onTransfer, onDetached, globalLocked = false } ) => {
	const { message } = App.useApp();
	// Which confirmation is open: 'delete' | 'archive' | 'transfer' | 'detach' | null.
	const [ confirming, setConfirming ] = useState( null );

	// SUB-COLLECTIONS (docs/plans/sub-collections.md §5.8, owner ruling D9):
	// a parent's delete and archive take its sub-collections with them, and
	// the confirm names each one with what it holds; a sub-collection is
	// transferred only with its parent, and may be detached.
	const subCollections = subCollectionsOf( collection );
	const parent = parentOf( collection );
	const transferBlocked = transferBlockedReason( collection );
	const familyItems = subCollections.reduce( ( sum, sub ) => sum + ( Number( sub.itemCount ) || 0 ), 0 );
	const [ newOwnerId, setNewOwnerId ] = useState( null );
	const name = collection?.name || '';
	const isArchived = collection?.status === 'archived';
	// Deleting someone else's collection asks for its name (m01); the owner,
	// and an admin, type the operation word as everywhere. A collection
	// without a name falls back to the word.
	const deleteByName = ! canTransfer && Boolean( name.trim() );
	const deleteWord = deleteByName ? name.trim().toLowerCase() : 'delete';

	// The collection's pending transfer offer (KPR-743): read when the tab
	// opens, replaced by the offer a transfer makes, cleared by Cancel. While
	// one waits, the row says so and offers Cancel instead of a second offer.
	// Asked only by whoever may transfer: the read is owner-only too (403).
	const offerCollectionId = canTransfer ? normalizeId( collection?._id ) : null;
	const [ pendingOffer, setPendingOffer ] = useState( null );
	const [ cancellingOffer, setCancellingOffer ] = useState( false );
	useEffect( () => {
		if ( ! offerCollectionId ) return undefined;
		let live = true;
		getCollectionTransferOffer( offerCollectionId )
			.then( offer => { if ( live ) setPendingOffer( offer ); } )
			.catch( () => { if ( live ) setPendingOffer( null ); } );
		return () => { live = false; };
	}, [ offerCollectionId ] );
	const handleCancelOffer = async () => {
		if ( ! pendingOffer?._id ) return;
		setCancellingOffer( true );
		try {
			await cancelTransferOffer( pendingOffer._id );
			setPendingOffer( null );
			message.success( 'Offer cancelled. Nothing changed.' );
		} catch ( error ) {
			message.error( transferOfferRefusal( error ) );
			getCollectionTransferOffer( offerCollectionId ).then( setPendingOffer ).catch( () => {} );
		} finally {
			setCancellingOffer( false );
		}
	};
	const offeredTo = pendingOffer
		? ( [ pendingOffer.recipient?.name, pendingOffer.recipient?.email ? `(${ pendingOffer.recipient.email })` : null ].filter( Boolean ).join( ' ' ) || 'someone' )
		: '';

	// Links this collection shares WITH copies allowed: deleting it keeps a
	// copy in each of those collections first, unless the owner says not to.
	// Read when the delete dialog opens, so the box is there only when it
	// means something.
	const collectionId = normalizeId( collection?._id );
	const [ copyLinks, setCopyLinks ] = useState( [] );
	const [ keepCopies, setKeepCopies ] = useState( true );
	useEffect( () => {
		if ( confirming !== 'delete' || ! collectionId ) return undefined;
		let live = true;
		setKeepCopies( true );
		getCollectionLinks( collectionId )
			.then( view => { if ( live ) setCopyLinks( ( view.outgoing || [] ).filter( link => link?.allowCopies === true ) ); } )
			.catch( () => { if ( live ) setCopyLinks( [] ); } );
		return () => { live = false; };
	}, [ confirming, collectionId ] );

	const close = () => { setConfirming( null ); setNewOwnerId( null ); };

	return <>
		<div className='settings-column'>
			{globalLocked ? (
				// The API refuses all three while the collection is global (409
				// collection_published): readers and other collections' lookups
				// depend on it. Retiring is the way out, on the Publishing tab.
				<Alert
					type='info'
					showIcon
					className='danger-zone__locked'
					message='This is a global collection'
					description='It cannot be archived, transferred or deleted while it is published or retiring. Retire it on the Publishing tab first.'
				/>
			) : null}
			<section className='form-panel form-panel--danger'>

				<div className='danger-row'>
					<div className='toggle-row__text'>
						<span className='toggle-row__label'>
							{isArchived ? 'Unarchive this collection' : 'Archive this collection'}
						</span>
						<span className='toggle-row__description'>
							{isArchived
								? 'Make items editable again for everyone with access.'
								: 'Keeps everything visible, but items become read-only for everyone. You can unarchive later.'}
						</span>
					</div>
					{isArchived ? (
						<Button onClick={onUnarchive}>Unarchive collection</Button>
					) : (
						<Button danger disabled={globalLocked} onClick={() => setConfirming( 'archive' )}>Archive collection…</Button>
					)}
				</div>

				{canDetach( collection ) ? (
					<div className='danger-row'>
						<div className='toggle-row__text'>
							<span className='toggle-row__label'>Detach from { parent?.name || 'its parent collection' }</span>
							<span className='toggle-row__description'>
								Make this a collection of its own again. It keeps its own cards, items and sharing, and the time zone it
								counts its days in now, but no longer uses the parent collection&rsquo;s cards, and access given on the
								parent no longer reaches it.
							</span>
						</div>
						<Button danger onClick={() => setConfirming( 'detach' )}>
							Detach…
						</Button>
					</div>
				) : null}

				{canTransfer ? <div className='danger-row'>
					<div className='toggle-row__text'>
						<span className='toggle-row__label'>Transfer ownership</span>
						<span className='toggle-row__description' data-testid='transfer-row-description'>
							{ pendingOffer
								? `Offered to ${ offeredTo }. It stays yours until they accept; the offer ends on ${ formatDisplayDate( pendingOffer.expiresAt ) }.`
								: ( transferBlocked || ( subCollections.length
									? `Offer this collection and its ${ formatCount( subCollections.length, 'sub-collection' ) } to someone else. They become the owner when they accept, and you keep only whatever access has been granted to you separately.`
									: 'Offer this collection to someone else. They become the owner when they accept, and you keep only whatever access has been granted to you separately.' ) ) }
						</span>
					</div>
					{ pendingOffer ? (
						<Button onClick={handleCancelOffer} loading={cancellingOffer}>
							Cancel offer
						</Button>
					) : (
						<Button danger disabled={globalLocked || Boolean( transferBlocked ) || isArchived} onClick={() => setConfirming( 'transfer' )}>
							Transfer ownership…
						</Button>
					) }
				</div> : null}

				<div className='danger-row'>
					<div className='toggle-row__text'>
						<span className='toggle-row__label'>Delete this collection</span>
						<span className='toggle-row__description'>
							{ subCollections.length
								? `Deletes the collection, its ${ formatCount( subCollections.length, 'sub-collection' ) } and every item in them. You can restore each from Recently deleted on the Collections page for 30 days. After that they and everything in them are deleted for good.`
								: 'Deletes the collection and every item in it. You can restore it from Recently deleted on the Collections page for 30 days. After that it and everything in it is deleted for good.' }
						</span>
					</div>
					<Button danger disabled={globalLocked} onClick={() => setConfirming( 'delete' )}>
						Delete collection…
					</Button>
				</div>

			</section>
		</div>

		<ConfirmWordModal
			open={confirming === 'archive'}
			word='archive'
			title='Archive this collection?'
			okText='Archive'
			onOk={() => { close(); onArchive(); }}
			onCancel={close}
		>
			<p>
				Archiving <strong>{name}</strong> makes all of its items read-only
				for everyone — no new items, edits, or deletions — until it is
				unarchived.
				{ subCollections.length ? ` Its ${ formatCount( subCollections.length, 'sub-collection' ) } are archived with it, and come back with it.` : '' }
			</p>
			{ ! ownsIt ? <p className='meta-line'>Its owner is told that you archived it.</p> : null }
		</ConfirmWordModal>

		<ConfirmWordModal
			open={confirming === 'transfer'}
			word='transfer'
			title='Transfer ownership?'
			okText='Send offer'
			extraValid={Boolean( newOwnerId )}
			onOk={() => {
				const target = newOwnerId;
				close();
				Promise.resolve( onTransfer( target ) ).then( offer => { if ( offer ) setPendingOffer( offer ); } );
			}}
			onCancel={close}
		>
			<p>
				Transferring <strong>{name}</strong> offers it to the person below.
				They become its owner when they accept, within 14 days; until then
				nothing changes. Once they accept, you keep only whatever access has
				been granted to you separately — this may lock you out of the
				collection.
			</p>
			<div className='mb-2'>
				<UserSearchSelect
					value={newOwnerId}
					onChange={setNewOwnerId}
					placeholder='New owner — search collaborators, or type a full email address'
					style={{ width: '100%' }}
				/>
			</div>
		</ConfirmWordModal>

		<ConfirmWordModal
			open={confirming === 'delete'}
			word={deleteWord}
			label={deleteByName ? name.trim() : null}
			title='Delete this collection?'
			okText={subCollections.length ? `Delete ${ subCollections.length + 1 } collections` : 'Delete'}
			onOk={() => { const copyToLinks = ! copyLinks.length || keepCopies; close(); onDelete( { copyToLinks } ); }}
			onCancel={close}
		>
			{subCollections.length ? (
				<div className='danger-zone__family' data-testid='delete-family'>
					<p>
						Deleting <strong>{name}</strong> also deletes its { formatCount( subCollections.length, 'sub-collection' ) } and
						everything in them. You can restore each from Recently deleted on the Collections page for 30 days. After that they and everything in them are deleted for good.
					</p>
					<ul className='danger-zone__family-rows'>
						{subCollections.map( sub => (
							<li key={sub._id}>
								<RecordBadge kind='collection' record={sub} title={sub.name} size='xs' />
								<span className='meta-line'>
									{ joinMeta( formatCount( sub.itemCount, 'item' ), formatCount( sub.cardIds.size, 'card' ) ) || 'Empty' }
								</span>
							</li>
						) )}
					</ul>
					<p className='meta-line'>
						{ `${ subCollections.length + 1 } collections` }{ familyItems ? ` · at least ${ formatCount( familyItems, 'item' ) } in the sub-collections` : '' }
					</p>
				</div>
			) : (
				<p>
					Deleting <strong>{name}</strong> also deletes all of its items.
					You can restore it, with its items, from Recently deleted on the
					Collections page for 30 days. After that it and everything in it is deleted for good.
				</p>
			)}
			{ ! ownsIt ? <p className='meta-line'>Its owner is told that you deleted it.</p> : null }
			{copyLinks.length ? (
				<div className='danger-zone__link-copies' data-testid='delete-link-copies'>
					<Checkbox checked={keepCopies} onChange={event => setKeepCopies( event.target.checked )}>
						Let linked collections keep copies
					</Checkbox>
					<p className='meta-line'>
						{copyLinks.length === 1
							? `${copyLinks[ 0 ].target?.name || 'A linked collection'} may keep copies of what it references here.`
							: `${copyLinks.length} linked collections may keep copies of what they reference here.`}
						{' '}Ticked, those copies are made before the delete. Unticked, their references show "No longer available" instead.
					</p>
				</div>
			) : null}
		</ConfirmWordModal>

		<DetachSubCollectionModal
			open={confirming === 'detach'}
			collection={collection}
			parent={parent}
			onClose={close}
			onDetached={onDetached}
		/>
	</>;
};



/**
 * The Facets setting: which card types this collection offers as a `@<card-key>`
 * filter (docs/plans/card-editor-dates-groups-filters.md Part 5, Block B).
 *
 * Left alone it is AUTOMATIC — a card two or more differently named lookups
 * point at earns its facet on its own, which is the case the feature exists
 * for and needs no setting at all. Naming cards here is the override for the
 * collection that wants "People ▾" from the first element onwards, and the
 * options are exactly the cards this collection's lookups already point at:
 * a facet over a card nothing references would match nothing.
 */
/**
 * Stored setting values as the options they mean. A setting written before
 * deterministic filters names a card by its KEY (`people`, `@people`); the
 * options name it by id (`<id>`, `@<id>`, ruling E11). A stored key shows as
 * its option, and the next save writes the id. Order kept, duplicates dropped;
 * a value no option knows stays as stored.
 */
export const asOptionValues = ( stored, options ) => [ ...new Set( ( stored || [] ).map( entry => {
	const option = ( options || [] ).find( candidate => candidate.value === entry )
		|| ( options || [] ).find( candidate => Array.isArray( candidate.aliases ) && candidate.aliases.includes( entry ) )
		// A chip setting names an element by its ids (⚑ K5): any one of them
		// names the chip (a card since added or removed changes the set).
		|| ( options || [] ).find( candidate => candidate.field && quickFilterKeyNames( candidate.field, entry ) );
	return option ? option.value : entry;
} ) ) ];

const FormFacets = ( { collection, handleChange } ) => {

	const cardDefinitions = useOptionalCardDefinitions();
	const allCards = cardDefinitions?.cards;
	const getElementsForCard = cardDefinitions?.getElementsForCard;

	const options = useMemo( () => {
		const cards = allCards || [];
		const memberIds = getCollectionMemberCardIds( collection, cards );
		const byId = new Map( cards.map( card => [ normalizeId( card?._id ), card ] ).filter( ( [ id ] ) => !! id ) );
		const aliasesOf = uniqueCardAliases( cards );
		const targets = new Set();
		memberIds.forEach( cardId => {
			const elements = ( typeof getElementsForCard === 'function' ? getElementsForCard( cardId ) : byId.get( cardId )?.elements ) || [];
			elements.forEach( element => {
				if ( element?.dataType !== 'card-lookup' ) return;
				const target = normalizeId( element?.options?.lookupCardId );
				if ( target ) targets.add( target );
			} );
		} );
		return [ ...targets ]
			.map( id => byId.get( id ) )
			.filter( card => card && cardRefOf( card ) )
			// The toolbar's rule: a key two cards share names neither.
			.map( card => ( { value: cardRefOf( card ), aliases: aliasesOf( card ), label: card?.label?.plural || card?.name || cardRefOf( card ) } ) )
			.sort( ( a, b ) => a.label.localeCompare( b.label ) );
	}, [ collection, allCards, getElementsForCard ] );

	const value = useMemo( () => asOptionValues( [ ...getCollectionFacetCardKeys( collection ) ], options ), [ collection, options ] );

	// The chips this collection can actually offer, derived the same way the
	// toolbar derives them — every facet, plus every member-card element an
	// editor promoted with `options.quickFilter`. A facet is stored by its
	// key (`@<card id>`); an element by its ids (element ids step 3, ⚑ K5:
	// `#k7f3q2xa`, or `#a|#b` where several cards each have one of that
	// name — quickFilterSettingKey), so a pin follows the element through a
	// rename. A setting saved by the element's name still finds its chip,
	// and is written as the ids on the next save.
	const chipOptions = useMemo( () => {
		const cards = allCards || [];
		const fields = buildFilterFields( {
			cards,
			cardIds: getCollectionMemberCardIds( collection, cards ),
			getElementsForCard,
			facetCardKeys: getCollectionFacetCardKeys( collection ),
		} );
		// The Tags chip (docs/plans/tags.md §5.2) is every collection's to pin
		// or hide, like any chip: it is offered on the toolbar wherever there
		// is a tag to pick.
		return [
			...quickFilterFields( fields ).map( field => ( { value: quickFilterSettingKey( field ), aliases: [ ...new Set( [ field.key, ...( field.aliases || [] ) ] ) ], field, label: quickFilterLabel( field ) } ) ),
			{ value: 'tags', label: 'Tags' },
		];
	}, [ collection, allCards, getElementsForCard ] );

	const pinned = useMemo( () => asOptionValues( getCollectionQuickFilterPins( collection ), chipOptions ), [ collection, chipOptions ] );
	const hidden = useMemo( () => asOptionValues( [ ...getCollectionHiddenQuickFilters( collection ) ], chipOptions ), [ collection, chipOptions ] );

	// Nothing in this collection points at another card and nothing is
	// promoted: there is no chip to configure, so the panel says nothing
	// rather than offering empty pickers.
	if ( ! options.length && ! value.length && ! chipOptions.length && ! pinned.length && ! hidden.length ) return null;

	return (
		<section className='form-panel'>
			<div className='micro-label form-panel__kicker'>Filter chips</div>
			<p className='form-panel__hint'>
				A facet filters on a card type through every lookup that points at
				it, whatever each element is called — one “People” chip instead of
				three fields. Left empty, any card two or more differently named
				lookups point at gets one automatically.
			</p>
			<Select
				mode='multiple'
				allowClear
				size='large'
				className='w-100'
				id='collection-facets'
				aria-label='Facets'
				placeholder='Automatic'
				value={value}
				options={options}
				onChange={next => handleChange( 'facets', toCollectionFacets( next ) )}
			/>
			<p className='form-panel__hint'>
				The toolbar shows the chips that match what the list is filtered to.
				Pinned chips are always offered, in this order, whatever card the
				list is showing; hidden ones are never offered. Both are
				presentation only — a hidden chip’s filter still applies, and still
				shows under the toolbar where it can be cleared.
			</p>
			<Select
				mode='multiple'
				allowClear
				size='large'
				className='w-100'
				id='collection-quick-filters-pinned'
				aria-label='Pinned chips'
				placeholder='Pinned chips'
				value={pinned}
				options={chipOptions}
				onChange={next => handleChange( 'quickFilters', toCollectionQuickFilters( next, hidden ) )}
			/>
			<Select
				mode='multiple'
				allowClear
				size='large'
				className='w-100'
				id='collection-quick-filters-hidden'
				aria-label='Hidden chips'
				placeholder='Hidden chips'
				value={hidden}
				options={chipOptions}
				onChange={next => handleChange( 'quickFilters', toCollectionQuickFilters( pinned, next ) )}
			/>
		</section>
	);
};


/** "3 items hold files in file elements. They stay stored and are hidden while attachments are off." */
export const attachmentsOffSentence = boundItems => `${ formatCount( boundItems, 'item' ) } ${ boundItems === 1 ? 'holds' : 'hold' } files in file elements. They stay stored and are hidden while attachments are off.`;

const FormSettings = ( props ) => {

	// (read-only disabling comes from the enclosing antd <Form disabled>)
	// Ownership/access moved to the header's ⋯ menu — this tab holds only
	// what Save changes actually saves.
	const { collection, handleChange, readOnly = false, footer = null } = props;
	const { modal } = App.useApp();

	// Turning attachments OFF (PR 10b, the 10b contract): first how many items
	// hold files in file elements (managers only), said before the switch
	// moves — those files stay stored and are hidden while the switch is off.
	// None, or an answer that does not come, and the switch just moves.
	const handleAttachmentsChange = async ( name, value ) => {
		if ( value !== false || collection?.allowAttachments !== true || ! collection?._id ) return handleChange( name, value );
		let bound = 0;
		try {
			const summary = await getCollectionAttachmentsSummary( collection._id );
			bound = Number( summary?.boundItems ) || 0;
		} catch {
			bound = 0;
		}
		if ( bound <= 0 ) return handleChange( name, value );
		modal.confirm( {
			title: 'Turn attachments off?',
			content: attachmentsOffSentence( bound ),
			okText: 'Turn attachments off',
			cancelText: 'Keep them on',
			onOk: () => handleChange( name, false ),
		} );
		return undefined;
	};

	return <>
		<div className='settings-column'>

			<section className='form-panel'>
				<div className='micro-label form-panel__kicker'>Basics</div>
				<InputText elementName='name' elementLabel='Name' value={collection.name} onChange={handleChange} showSkeleton={false} />
				<InputTextArea fi={true} elementName='detail' elementLabel='Description' value={collection.detail} onChange={handleChange} />
				{parentOf( collection ) ? (
					// A sub-collection's place in its family: read-only here —
					// it changes only by detaching (the Danger zone).
					<div className='form-panel__fact' data-testid='settings-part-of'>
						<span className='micro-label'>Part of</span>
						<RecordBadge
							kind='collection'
							record={parentOf( collection )}
							title={parentOf( collection ).name}
							size='xs'
							to={parentOf( collection ).readable ? `/collections/${ parentOf( collection )._id }/` : undefined}
						/>
					</div>
				) : null}
			</section>

			<section className='form-panel'>
				<div className='micro-label form-panel__kicker'>Identity</div>
				<InputIconSelect
					elementName='icon'
					elementLabel='Icon and color'
					value={collection.icon}
					onChange={handleChange}
					colorElementName='color'
					colorValue={collection.color}
					onColorChange={handleChange}
					recordKind='collection'
				/>
			</section>

			{/* The zone its charts count days in (analytics q06). Only once the
			    API has said where the zone comes from: absent on a collection
			    not saved yet, and on an API that predates the setting. */}
			{collection?.timezoneResolved ? (
				<section className='form-panel'>
					<CollectionTimezoneField collection={collection} onChange={handleChange} readOnly={readOnly} />
				</section>
			) : null}

			{/* The currency new money on its cards starts in (ruled L3). The
			    same rule: only once the API has said where it comes from. */}
			{collection?.currencyResolved ? (
				<section className='form-panel'>
					<CollectionCurrencyField collection={collection} onChange={handleChange} readOnly={readOnly} />
				</section>
			) : null}

			<section className='form-panel'>
				{/* Every boolean is a toggle row (InputSwitch renders one). */}
				<InputSwitch
					elementName='allowAttachments'
					elementLabel='Allow attachments'
					description='Items in this collection can carry uploaded files.'
					value={collection.allowAttachments}
					onChange={handleAttachmentsChange}
					first
				/>
			</section>

			<FormFacets collection={collection} handleChange={handleChange} />

			{footer}
		</div>
	</>;

};



// eslint-disable-next-line no-unused-vars -- kept for the temporarily hidden Card Builder tab
const FormCards = ( props ) => {

	const { collection, setCollection, } = props;

	// const [ titleElements, setTitleElements ] = useState( collection.definition.titleElements || ['one','two','three',] );

	const elementHelpContent = <>
		<span>
			Elements make your collections more meaningful by specifying the 
			types of information in each item. These influence how filters 
			work and how data is displayed. 
		</span>
	</>;



	const handleFEChange = ( elementName, propName, value ) => {
			
		setCollection( prevState => {
			const elements = prevState.definition.elements;
			let foundMatch = false;
			for ( let i = 0; i < elements.length; ++i ) {
				if ( elements[ i ].name === elementName ) {
					foundMatch = true;
					
					// PROP - name
					if ( propName === 'name' ) { // renaming element definition
						if ( ! elements[ i ].prevName ) {
							// preserve previous key before renaming
							elements[ i ].prevName = elementName;
						}
						else {
							if ( value === elements[ i ].prevName ) {
								// back to original, delete prevName prop
								delete elements[ i ].prevName;
							}
						}
					}

					// PROP - labelSingular, labelPlural
					else if ( propName === 'labelSingular' || propName === 'labelPlural' ) {
						if ( ! elements[ i ].label ) elements[ i ].label = {};
						if ( propName === 'labelSingular' ) {
							elements[ i ].label.singular = value;
							break;
						}
						if ( propName === 'labelPlural' ) {
							elements[ i ].label.plural = value;
							break;
						}
					}

					// PROP - choices 
					else if ( propName === 'choices' ) {
						//
						_.set( elements[ i ], 'options.choices', value );
					}

					// ELSE - update prop
					elements[ i ][ propName ] = value;
					break;
				}
			}

			// no match found, add new element to array
			if ( ! foundMatch ) {
				// elements.push( value );
			}

			return cleanPrevKeys( { ...prevState, definition: { ...prevState.definition, elements: elements } } );
		} );
		
	};

	const cleanPrevKeys = ( obj, forceDelete ) => {
		// TODO - temporary to fix issue with adding new choices to element

		const elements = obj.definition.elements;

		for ( var eIdx = 0; eIdx < elements.length; ++eIdx ) {
			if ( elements[ eIdx ].hasOwnProperty( 'choices' ) ) {
				for ( var cIdx = 0; cIdx < elements[ eIdx ].choices.length; ++cIdx ) {
					if ( forceDelete || 
						elements[ eIdx ].choices[ cIdx ].prevValue === '' ||
						elements[ eIdx ].choices[ cIdx ].prevValue === elements[ eIdx ].choices[ cIdx ].value ) {
						delete elements[ eIdx ].choices[ cIdx ].prevValue;
					}
				}
			}
		}

		obj.definition.elements = elements;

		return obj;
	};

	const handleNewElement = () => {
		
		setCollection( { ...collection, definition: { ...collection.definition, elements: [ ...collection.definition.elements, {
			name: '',
			label: {
				singular: '',
				plural: ''
			},
			options: {
				choices: []
			}
		} ] } } );

	};

	const handleDeleteElement = ( elementName ) => {
		setCollection( prevState => ( { ...prevState, definition: { ...prevState.definition, elements: prevState.definition.elements.filter( e => e.name !== elementName ) } } ) );
	};

	const handleDragEnd = ( event ) => {
		//
		const { active, over, } = event;

		if ( active.id !== over.id ) {
			setCollection( prevState => {
				const elements = prevState.definition.elements;
				const activeIdx = elements.map( e => e.label.singular ).indexOf( active.id );
				const overIdx = elements.map( e => e.label.singular ).indexOf( over.id );
				
				return {
					...prevState,
					definition: {
						...prevState.definition,
						elements: arrayMove( elements, activeIdx, overIdx )
					}
				};
			} );
		}

	};



	return <>
		<div className='form-bundle'>

			<h4>Card Preview</h4>

			<Card style={{ width: '18em', backgroundColor: 'var(--chip-bg)', borderColor: 'var(--border)' }}>
				<span>{collection?.name}</span>

				{collection.definition.elements.map( element => {
					//

					return ( <Space key={element.name}>
						{dataTypes.find( e => e.name === element.dataType )?.bsIconJSX}
						<Skeleton.Input active={false} size='small' className='m-1' />
					</Space> );
				} )}
			</Card>



			<div className='d-flex'>
				<h4 style={{flexGrow: 1,}}>
					Elements
					<span className='mx-2' style={{fontSize: '70%',}}>
						({collection?.definition?.elements?.length})
					</span>
				</h4>

				<Popover content={elementHelpContent} trigger='click'>
					<span style={{fontSize: '150%',}}><HELP_QUESTION_MARK /></span>
				</Popover>
			</div>



			<DndContext
				collisionDetection={closestCenter}
				// onDragEnd={handleDragEnd}
				// using onDragOver to prevent 'bounce back' animation on drop (viewed in Brave on mobile mode)
				onDragOver={handleDragEnd}
			>
				<SortableContext
					items={collection.definition.elements.map( e => e.label.singular )}
					strategy={verticalListSortingStrategy}
				>
					{collection.definition.elements.map( ( element, idx ) => <CollectionFormElement
						key={idx} 
						idx={idx}
						id={element.label.singular}
						element={element} 
						value={element} 
						onChange={handleFEChange} 
						onDelete={handleDeleteElement} 
					/> )}
				</SortableContext>
			</DndContext>


			<div className='d-flex'>
				<Button className='flex-grow-1 flex-md-grow-0' type='primary' title='Add new element' onClick={handleNewElement}>
					<PLUS /> New element
				</Button>
			</div>



			{/* <h4>Title</h4> */}
			{/* <DndContext
				collisionDetection={closestCenter}
				onDragOver={() => {}}
			>
				<SortableContext
					items={titleElements.map( e => e )}
					strategy={horizontalListSortingStrategy}
				>
					{titleElements.map( e => <span key={e}>{e}</span> )}
				</SortableContext>
			</DndContext> */}
				
		</div>
	</>;

};
